import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import * as XLSX from 'xlsx';
import { Card } from '@/components/Card/Card';
import { Table } from '@/components/Table/Table';
import {
  fetchFilterValues,
  fetchReportData,
  relatorioEtiquetasKeys,
} from '@/services/relatorioEtiquetasService';
import type {
  IRelatorioEtiquetasRow,
  IRelatorioEtiquetasSearch,
  TRelatorioEtiquetasFilterType,
} from '@/types/relatorioEtiquetas';
import { RelatorioEtiquetasForm } from './form';
import './etiquetas.css';

export const Route = createFileRoute('/_authenticated/relatorio/etiquetas/')({
  component: RelatorioEtiquetasPage,
});

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const FILTER_COLUMNS: Record<TRelatorioEtiquetasFilterType, string[]> = {
  pedido: ['Pedido', 'Código', 'Descrição', 'Peso Líquido', 'Caixas'],
  saida: ['Data de Saída', 'Código', 'Descrição', 'Peso Líquido', 'Caixas'],
  entrada: ['NF', 'Data de Entrada', 'Código', 'Descrição', 'Peso Líquido', 'Caixas'],
};

function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

function getPaginationPages(currentPage: number, totalPages: number): (number | 'ellipsis')[] {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [1];

  if (currentPage > 3) {
    pages.push('ellipsis');
  }

  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  for (let page = start; page <= end; page += 1) {
    pages.push(page);
  }

  if (currentPage < totalPages - 2) {
    pages.push('ellipsis');
  }

  if (totalPages > 1) {
    pages.push(totalPages);
  }

  return pages;
}

function RelatorioEtiquetasPage() {
  const [filterType, setFilterType] = useState<TRelatorioEtiquetasFilterType>('pedido');
  const [selectedFilter, setSelectedFilter] = useState('');
  const [activeSearch, setActiveSearch] = useState<IRelatorioEtiquetasSearch | null>(null);
  const [message, setMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const {
    data: filterValues = [],
    isLoading: isLoadingFilters,
    isError: isFilterValuesError,
  } = useQuery({
    queryKey: relatorioEtiquetasKeys.filterValues(filterType),
    queryFn: () => fetchFilterValues(filterType),
  });

  const {
    data: reportResult,
    isFetching: isSearching,
    isError: isReportError,
    dataUpdatedAt,
  } = useQuery({
    queryKey: activeSearch
      ? relatorioEtiquetasKeys.reportData(activeSearch)
      : [...relatorioEtiquetasKeys.all, 'reportData', 'idle'],
    queryFn: () => fetchReportData(activeSearch!),
    enabled: Boolean(activeSearch?.selectedFilter),
  });

  const reportData = useMemo(
    () => reportResult?.rows ?? [],
    [reportResult?.rows],
  );
  const totalRecords = reportResult?.totalRecords ?? 0;
  const tableColumns = FILTER_COLUMNS[filterType];

  const totalPages = Math.max(1, Math.ceil(reportData.length / pageSize));
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return reportData.slice(start, start + pageSize);
  }, [reportData, currentPage, pageSize]);

  const inicio = reportData.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const fim = Math.min(currentPage * pageSize, reportData.length);
  const paginationPages = getPaginationPages(currentPage, totalPages);

  const handleFilterTypeChange = (value: TRelatorioEtiquetasFilterType) => {
    setFilterType(value);
    setSelectedFilter('');
    setActiveSearch(null);
    setMessage('');
    setCurrentPage(1);
  };

  const handleSearch = () => {
    if (!selectedFilter) {
      setMessage('Selecione um filtro antes de buscar os dados.');
      return;
    }

    setActiveSearch({ filterType, selectedFilter });
    setMessage('');
    setCurrentPage(1);
  };

  const handleExport = () => {
    if (reportData.length === 0) {
      setMessage('Nenhum dado para exportar.');
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(reportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Relatorio');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `Relatorio_${filterType}_${selectedFilter}.xlsx`;
    link.click();

    setMessage('Exportação concluída.');
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const statusMessage = (() => {
    if (message) return message;
    if (isFilterValuesError) return 'Erro ao carregar valores do filtro.';
    if (isReportError) return 'Erro ao buscar dados.';
    if (activeSearch && !isSearching && dataUpdatedAt > 0) {
      if (reportData.length === 0) return 'Nenhum dado encontrado.';
      const registrosLabel = pluralize(totalRecords, 'registro', 'registros');
      const agrupamentosLabel = pluralize(reportData.length, 'agrupamento', 'agrupamentos');
      return `Foram encontrados ${totalRecords} ${registrosLabel} e ${reportData.length} ${agrupamentosLabel}.`;
    }
    return '';
  })();

  const messageClassName =
    isFilterValuesError || isReportError || statusMessage.includes('Erro')
      ? 'relatorio-etiquetas-message--error'
      : isSearching
        ? 'relatorio-etiquetas-message--loading'
        : 'relatorio-etiquetas-message--info';

  const renderCell = (row: IRelatorioEtiquetasRow, column: string): ReactNode => {
    const value = row[column as keyof IRelatorioEtiquetasRow];

    if (column === 'Código') {
      return <span className="relatorio-etiquetas-codigo">{value}</span>;
    }

    if (column === 'Peso Líquido') {
      return <span className="relatorio-etiquetas-peso">{value} kg</span>;
    }

    if (column === 'Caixas') {
      return (
        <span className="relatorio-etiquetas-caixas-badge">
          {value} CX
        </span>
      );
    }

    return value ?? '';
  };

  return (
    <div className="relatorio-etiquetas-container">
      <div className="relatorio-etiquetas-content">
        <RelatorioEtiquetasForm
          filterType={filterType}
          selectedFilter={selectedFilter}
          filterValues={filterValues}
          isLoadingFilters={isLoadingFilters}
          isSearching={isSearching}
          canExport={reportData.length > 0}
          onFilterTypeChange={handleFilterTypeChange}
          onSelectedFilterChange={setSelectedFilter}
          onSearch={handleSearch}
          onExport={handleExport}
        />

        {statusMessage && (
          <div className={`relatorio-etiquetas-message ${messageClassName}`}>
            {statusMessage}
          </div>
        )}

        <Card className="relatorio-etiquetas-table-card">
          <Card.Header>
            <h3>
              Resultados
              {reportData.length > 0
                ? ` (${reportData.length.toLocaleString('pt-BR')} ${pluralize(
                    reportData.length,
                    'agrupamento',
                    'agrupamentos',
                  )})`
                : ''}
            </h3>
          </Card.Header>

          <Card.Content>
            <div className="relatorio-etiquetas-table-container">
              <Table>
                <caption className="sr-only">Relatório de etiquetas</caption>
                <Table.Header>
                  <Table.Row>
                    {tableColumns.map((column) => (
                      <Table.Head
                        key={column}
                        scope="col"
                      >
                        {column}
                      </Table.Head>
                    ))}
                  </Table.Row>
                </Table.Header>
                <Table.Content>
                  {paginatedData.length > 0 ? (
                    paginatedData.map((row, rowIndex) => (
                      <Table.Row key={rowIndex}>
                        {tableColumns.map((column) => (
                          <Table.Cell key={column}>{renderCell(row, column)}</Table.Cell>
                        ))}
                      </Table.Row>
                    ))
                  ) : (
                    <Table.Row>
                      <Table.Cell
                        colSpan={tableColumns.length}
                        className="relatorio-etiquetas-no-data"
                      >
                        {isSearching
                          ? 'Carregando...'
                          : activeSearch
                            ? 'Nenhum dado encontrado'
                            : 'Selecione os filtros e clique em Buscar Dados'}
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Table.Content>
              </Table>
            </div>
          </Card.Content>

          {reportData.length > 0 && (
            <Card.Footer className="relatorio-etiquetas-pagination">
              <div className="relatorio-etiquetas-pagination__left">
                <label htmlFor="page-size">Itens por página:</label>
                <select
                  id="page-size"
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option
                      key={size}
                      value={size}
                    >
                      {size}
                    </option>
                  ))}
                </select>
                <p>
                  Mostrando <strong>{inicio.toLocaleString('pt-BR')}</strong> -{' '}
                  <strong>{fim.toLocaleString('pt-BR')}</strong> de{' '}
                  <strong>{reportData.length.toLocaleString('pt-BR')}</strong> registros
                </p>
              </div>

              <div className="relatorio-etiquetas-pagination__controls">
                <button
                  type="button"
                  className="relatorio-etiquetas-page-btn"
                  disabled={currentPage === 1 || isSearching}
                  onClick={() => handlePageChange(currentPage - 1)}
                  aria-label="Página anterior"
                >
                  ←
                </button>

                {paginationPages.map((page, index) =>
                  page === 'ellipsis' ? (
                    <span
                      key={`ellipsis-${index}`}
                      className="relatorio-etiquetas-page-ellipsis"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      type="button"
                      className={[
                        'relatorio-etiquetas-page-btn',
                        page === currentPage ? 'relatorio-etiquetas-page-btn--active' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => handlePageChange(page)}
                      disabled={isSearching}
                    >
                      {page}
                    </button>
                  ),
                )}

                <button
                  type="button"
                  className="relatorio-etiquetas-page-btn"
                  disabled={currentPage >= totalPages || isSearching}
                  onClick={() => handlePageChange(currentPage + 1)}
                  aria-label="Próxima página"
                >
                  →
                </button>
              </div>
            </Card.Footer>
          )}
        </Card>
      </div>
    </div>
  );
}
